import React, { useEffect, useRef, useState, useMemo } from 'react';
import axios from 'axios';
import { createChart, AreaSeries } from 'lightweight-charts';
import { API_BASE_URL } from '../../config';

function getAuthHeader() {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

const RANGE_OPTIONS = [
  { label: '1M', days: 30 },
  { label: '3M', days: 90 },
  { label: '6M', days: 180 },
  { label: '1Y', days: 365 },
  { label: 'All', days: 1825 },
];

function toLocalDateStr(iso) {
  return iso.slice(0, 10);
}

function buildDotGroups(predictions) {
  const map = new Map();
  for (const p of predictions) {
    if (!p.published_at) continue;
    const date = toLocalDateStr(p.published_at);
    const key = `${date}|${p.direction}`;
    if (!map.has(key)) map.set(key, { date, direction: p.direction, preds: [], analystIds: new Set() });
    const entry = map.get(key);
    entry.preds.push(p);
    entry.analystIds.add(p.analyst_name ?? p.analyst_id ?? p.id);
  }
  return [...map.values()].map(({ analystIds, ...rest }) => ({
    ...rest,
    uniqueAnalystCount: analystIds.size,
  }));
}

const DOT_COLOR = { bullish: '#16a34a', bearish: '#dc2626', neutral: '#9ca3af' };
const DOT_OFFSET_Y = { bullish: 18, bearish: -18, neutral: 0 };

function dotRadius(count) {
  return Math.min(6 + (count - 1) * 3, 16);
}

export default function PriceTimelineChart({ ticker, exchange, predictions }) {
  const containerRef = useRef(null);
  const chartRef = useRef(null);
  const seriesRef = useRef(null);

  const [range, setRange] = useState(365);
  const [priceData, setPriceData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [overlayTick, setOverlayTick] = useState(0);
  const [hoveredGroup, setHoveredGroup] = useState(null); // { group, x, y }

  // Fetch price history when range/symbol changes
  useEffect(() => {
    const fetchPrices = async () => {
      setLoading(true);
      try {
        const res = await axios.get(
          `${API_BASE_URL}/api/research/symbols/${exchange}/${ticker}/price-history/?days=${range}`,
          { headers: getAuthHeader() },
        );
        setPriceData(res.data);
      } catch (err) {
        setPriceData([]);
      } finally {
        setLoading(false);
      }
    };
    fetchPrices();
  }, [ticker, exchange, range]);

  // Create chart once
  useEffect(() => {
    if (!containerRef.current) return;

    const chart = createChart(containerRef.current, {
      layout: {
        background: { color: '#ffffff' },
        textColor: '#374151',
        fontFamily: 'Inter, system-ui, sans-serif',
        fontSize: 11,
        attributionLogo: false,
      },
      grid: {
        vertLines: { color: '#f3f4f6' },
        horzLines: { color: '#f3f4f6' },
      },
      crosshair: { mode: 1 },
      rightPriceScale: { borderColor: '#e5e7eb' },
      timeScale: { borderColor: '#e5e7eb', timeVisible: false },
      handleScroll: true,
      handleScale: true,
    });
    chartRef.current = chart;

    const series = chart.addSeries(AreaSeries, {
      lineColor: '#3b82f6',
      topColor: 'rgba(59, 130, 246, 0.12)',
      bottomColor: 'rgba(59, 130, 246, 0)',
      lineWidth: 2,
      priceLineVisible: false,
      lastValueVisible: false,
    });
    seriesRef.current = series;

    // Re-render overlay dots on pan/zoom
    const onRangeChange = () => setOverlayTick((t) => t + 1);
    chart.timeScale().subscribeVisibleLogicalRangeChange(onRangeChange);

    const ro = new ResizeObserver(() => {
      if (containerRef.current) {
        chart.applyOptions({ width: containerRef.current.clientWidth });
        setOverlayTick((t) => t + 1);
      }
    });
    ro.observe(containerRef.current);

    return () => {
      ro.disconnect();
      chart.timeScale().unsubscribeVisibleLogicalRangeChange(onRangeChange);
      chart.remove();
      chartRef.current = null;
      seriesRef.current = null;
    };
  }, []);

  // Update price series data
  useEffect(() => {
    if (!seriesRef.current) return;
    const data = priceData
      .map((p) => ({ time: p.date, value: parseFloat(p.close) }))
      .filter((p) => !isNaN(p.value));
    seriesRef.current.setData(data);
    if (data.length) chartRef.current?.timeScale().fitContent();
    setOverlayTick((t) => t + 1);
  }, [priceData]);

  // Trigger overlay re-render when predictions change
  useEffect(() => {
    setOverlayTick((t) => t + 1);
  }, [predictions]);

  const priceByDate = useMemo(() => {
    const map = {};
    for (const p of priceData) map[p.date] = parseFloat(p.close);
    return map;
  }, [priceData]);

  const dotGroups = useMemo(() => buildDotGroups(predictions), [predictions]);

  // Compute pixel positions for all dots — recomputes on pan/zoom/resize via overlayTick
  const dots = useMemo(() => {
    if (!chartRef.current || !seriesRef.current) return [];
    const chart = chartRef.current;
    const series = seriesRef.current;
    const result = [];
    for (const group of dotGroups) {
      const price = priceByDate[group.date];
      if (price == null) continue;
      const x = chart.timeScale().timeToCoordinate(group.date);
      const baseY = series.priceToCoordinate(price);
      if (x == null || baseY == null) continue;
      result.push({
        group,
        x,
        y: baseY + (DOT_OFFSET_Y[group.direction] ?? 0),
        r: dotRadius(group.uniqueAnalystCount),
        color: DOT_COLOR[group.direction] ?? '#9ca3af',
      });
    }
    return result;
  // overlayTick is intentionally in deps to retrigger coordinate mapping on chart changes
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dotGroups, priceByDate, overlayTick]);

  const hasData = priceData.length > 0;

  return (
    <div className="bg-white rounded-xl border border-gray-100 p-4">
      {/* Header + range buttons */}
      <div className="flex items-center justify-between mb-3">
        <span className="text-sm font-semibold text-gray-700">Price History</span>
        <div className="flex gap-1">
          {RANGE_OPTIONS.map(({ label, days }) => (
            <button
              key={label}
              onClick={() => setRange(days)}
              className={`px-2.5 py-1 text-xs rounded-md font-medium transition-colors ${
                range === days
                  ? 'bg-gray-900 text-white'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* Chart container */}
      <div className="relative">
        <div ref={containerRef} style={{ height: 280, width: '100%' }} />

        {/* SVG overlay — consolidated prediction dots */}
        {hasData && dots.length > 0 && (
          <svg
            className="absolute inset-0 pointer-events-none"
            style={{ width: '100%', height: 280, zIndex: 5 }}
          >
            {dots.map(({ group, x, y, r, color }) => (
              <circle
                key={`${group.date}|${group.direction}`}
                cx={x}
                cy={y}
                r={r}
                fill={color}
                fillOpacity={0.85}
                stroke="white"
                strokeWidth={1.5}
                style={{ cursor: 'pointer', pointerEvents: 'auto' }}
                onMouseEnter={(e) => {
                  const rect = containerRef.current?.getBoundingClientRect();
                  if (!rect) return;
                  setHoveredGroup({
                    group,
                    x: e.clientX - rect.left,
                    y: e.clientY - rect.top,
                  });
                }}
                onMouseLeave={() => setHoveredGroup(null)}
              />
            ))}
          </svg>
        )}

        {/* Loading overlay */}
        {loading && (
          <div className="absolute inset-0 flex items-center justify-center bg-white text-sm text-gray-400 animate-pulse">
            Loading price data…
          </div>
        )}

        {/* Empty state */}
        {!loading && !hasData && (
          <div className="absolute inset-0 flex items-center justify-center bg-white text-sm text-gray-400">
            No price history available for this symbol.
          </div>
        )}

        {/* Hover tooltip */}
        {hoveredGroup && (
          <div
            className="absolute bg-white border border-gray-200 rounded-lg shadow-lg p-3 text-xs z-10 pointer-events-none"
            style={{
              left: Math.min(hoveredGroup.x + 14, (containerRef.current?.clientWidth ?? 500) - 210),
              top: Math.max(hoveredGroup.y - 10, 0),
              maxWidth: 210,
            }}
          >
            <p className="text-gray-400 mb-2">
              {hoveredGroup.group.date} · {hoveredGroup.group.uniqueAnalystCount} analyst{hoveredGroup.group.uniqueAnalystCount > 1 ? 's' : ''} · {hoveredGroup.group.preds.length} prediction{hoveredGroup.group.preds.length > 1 ? 's' : ''}
            </p>
            {hoveredGroup.group.preds.map((p) => (
              <div key={p.id} className="mb-1.5 last:mb-0 flex items-center gap-2 min-w-0">
                <span className={`shrink-0 text-[10px] px-1.5 py-0.5 rounded font-semibold ${
                  p.direction === 'bullish' ? 'bg-emerald-100 text-emerald-700'
                  : p.direction === 'bearish' ? 'bg-red-100 text-red-700'
                  : 'bg-gray-100 text-gray-600'
                }`}>
                  {p.direction}
                </span>
                <span className="font-medium text-gray-800 truncate">{p.analyst_name}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Legend */}
      {hasData && predictions.length > 0 && (
        <div className="flex items-center gap-4 mt-3 text-xs text-gray-400">
          <span className="flex items-center gap-1.5">
            <span className="w-2 h-2 bg-emerald-500 rounded-full" /> Bullish call
          </span>
          <span className="flex items-center gap-1.5">
            <span className="w-2 h-2 bg-red-400 rounded-full" /> Bearish call
          </span>
          <span className="flex items-center gap-1.5">
            <span className="w-2 h-2 bg-gray-300 rounded-full" /> Neutral
          </span>
        </div>
      )}
    </div>
  );
}
